import { useCallback } from 'react'
import { api } from '@/lib/api/requests'
import { useLazyResource, type LazyResource } from './useLazyResource'

export interface PlatformStats {
  clubs: number
  tournamentsCompleted: number
  ratedPlayers: number
  courts: number
  matchesPlayed: number
}

/**
 * Public platform-wide counts (`GET /stats/public` → `{ clubs,
 * tournamentsCompleted, ratedPlayers, courts, matchesPlayed }`, real + cached,
 * no auth). One shared read (the single source of truth — components consume
 * this, never a leaf fetch) for the home proof strip, About, and Solutions.
 * `clubs` stays in the type even though no surface renders it today (see
 * docs/ux/2026-09-23-platform-counters.md §3.1). Eager by default so the
 * strip resolves on mount; on error the callers render static facts, never
 * invented or zero counts.
 */
export function usePlatformStats(enabled = true): LazyResource<PlatformStats> {
  const fetcher = useCallback(async (): Promise<PlatformStats> => {
    const response = await api.get<Partial<PlatformStats>>('/stats/public', {
      handleAuthErrors: false,
    })
    return {
      clubs: Number(response?.clubs || 0),
      tournamentsCompleted: Number(response?.tournamentsCompleted || 0),
      ratedPlayers: Number(response?.ratedPlayers || 0),
      courts: Number(response?.courts || 0),
      matchesPlayed: Number(response?.matchesPlayed || 0),
    }
  }, [])

  return useLazyResource(fetcher, enabled)
}
